// src/components/common/ProtectedRoute.tsx
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useAuth from '../../features/auth/hooks/useAuth';
import authService from '../../services/authService';
import { CircularProgress, Box, Alert } from '@mui/material';
import { shouldBypassAuth } from '../../config/devMode';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/** sessionStorage key set by apiClient when a session could not be refreshed. */
export const SESSION_EXPIRED_KEY = 'auth:expired';

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, bootstrapped } = useAuth();
  const location = useLocation();
  const devBypass = shouldBypassAuth();

  // Dev mode bypass - skip auth checks entirely in local development
  if (devBypass) {
    return (
      <>
        <Alert
          severity="warning"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 9999,
            boxShadow: 3,
          }}
        >
          DEV MODE: Auth bypassed
        </Alert>
        {children}
      </>
    );
  }

  // A stored token that has not been exchanged for a profile yet is NOT an
  // anonymous visitor. Wait for the bootstrap instead of redirecting — the
  // redirect-then-sign-in-again loop seen in recordings came from here.
  const awaitingBootstrap = !isAuthenticated && !bootstrapped && authService.isAuthenticated();

  if (isLoading || awaitingBootstrap) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // If not authenticated, redirect to login and remember where we were going
  if (!isAuthenticated) {
    let reason: string | undefined;
    try {
      if (sessionStorage.getItem(SESSION_EXPIRED_KEY)) {
        reason = 'expired';
        sessionStorage.removeItem(SESSION_EXPIRED_KEY);
      }
    } catch {
      /* storage unavailable */
    }
    return <Navigate to="/login" state={{ from: location, reason }} replace />;
  }

  // If authenticated, render children
  return <>{children}</>;
};

export default ProtectedRoute;
